import React from 'react';
import { Link } from 'react-router-dom';
import { FaCalculator, FaWeight, FaFire, FaUtensils, FaCalendarAlt, FaRulerVertical, FaTint, FaArrowRight } from 'react-icons/fa';
import SEO, { PageSEO } from '../../components/SEO';

const calculators = [
  {
    id: 'bmi',
    title: 'BMI Calculator',
    description: 'Calculate your Body Mass Index and see where you stand on the health scale.',
    icon: FaWeight,
    path: '/calculators/bmi',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    id: 'tdee',
    title: 'TDEE & Calorie Calculator',
    description: 'Find your Total Daily Energy Expenditure and daily calorie needs for your goals.',
    icon: FaFire,
    path: '/calculators/tdee',
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600',
  },
  {
    id: 'macro',
    title: 'Macro Calculator',
    description: 'Get personalized protein, carbs, and fat targets based on your goals.',
    icon: FaUtensils,
    path: '/calculators/macro',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    iconColor: 'text-green-600',
  },
  {
    id: 'weight-loss',
    title: 'Weight Loss Timeline',
    description: 'Estimate how long it will take to reach your goal weight safely.',
    icon: FaCalendarAlt,
    path: '/calculators/weight-loss-timeline',
    color: 'from-purple-500 to-indigo-600',
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
  },
  {
    id: 'ideal-weight',
    title: 'Ideal Body Weight',
    description: 'Calculate your ideal weight range based on height, age, and gender.',
    icon: FaRulerVertical,
    path: '/calculators/ideal-weight',
    color: 'from-pink-500 to-rose-600',
    bgColor: 'bg-pink-50',
    iconColor: 'text-pink-600',
  },
  {
    id: 'water',
    title: 'Water Intake Calculator',
    description: 'Find out how much water you should drink daily for optimal health.',
    icon: FaTint,
    path: '/calculators/water-intake',
    color: 'from-cyan-500 to-blue-500',
    bgColor: 'bg-cyan-50',
    iconColor: 'text-cyan-600',
  },
];

const FitnessCalculators = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.calculators} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-6">
              <FaCalculator className="text-3xl" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Free Fitness Calculators
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto">
              Use our science-backed calculators to understand your body better and plan your fitness journey effectively.
            </p>
          </div>
        </div>
      </section>

      {/* Calculators Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {calculators.map((calc) => (
              <Link
                key={calc.id}
                to={calc.path}
                className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-6 md:p-8">
                  <div className={`inline-flex items-center justify-center w-14 h-14 ${calc.bgColor} rounded-xl mb-5 group-hover:scale-110 transition-transform`}>
                    <calc.icon className={`text-2xl ${calc.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {calc.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {calc.description}
                  </p>
                  <div className="flex items-center text-primary-600 font-medium">
                    <span>Calculate Now</span>
                    <FaArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
                <div className={`h-1.5 bg-gradient-to-r ${calc.color} transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left`}></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Start Your Transformation?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Explore our personalized programs with expert coaching, nutrition plans, and workout programs.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center bg-accent-500 text-white px-8 py-4 rounded-xl hover:bg-accent-600 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
          >
            View Programs
            <FaArrowRight className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FitnessCalculators;
